import type { FormEvent, ReactNode, Ref } from 'react';
import { ArrowRight, MagnifyingGlass, MapPin } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type SearchFieldProps = {
  query: string;
  onQueryChange: (query: string) => void;
  onSubmit: () => void;
  /** Accessible name of the text field ("What job?"), localised. */
  queryLabel: string;
  placeholder?: string;
  /** The chosen place, or a prompt ("Anywhere in Finland"). Opens the search panel. */
  place?: ReactNode;
  /** True once a real place is chosen (not the prompt): together with a query it switches the button to the live count. */
  placeSelected?: boolean;
  onOpenPanel?: () => void;
  /** Accessible name of the place button ("Change location: Helsinki"). */
  placeLabel?: string;
  /** The button's label with no search context ("Search"). */
  submitLabel: ReactNode;
  /**
   * The live count for this search from the index, formatted ("4,177 jobs"). Shown on the button
   * once a query or a place is set; never a guess, so leave it out on pages with no search context.
   */
  liveCount?: ReactNode;
  /** Accessible name of the form landmark. */
  label: string;
  /** Goes to the text input, the field's focusable element. */
  ref?: Ref<HTMLInputElement>;
  className?: string;
};

/**
 * The pill search bar: what, where, and a go button that is the screen's primary action. 46px tall
 * (44 on phones); 16px input text so iOS never zooms in.
 */
export function SearchField({
  query,
  onQueryChange,
  onSubmit,
  queryLabel,
  placeholder,
  place,
  placeSelected = false,
  onOpenPanel,
  placeLabel,
  submitLabel,
  liveCount,
  label,
  className,
  ref,
}: SearchFieldProps) {
  const showCount = liveCount != null && (query.trim() !== '' || placeSelected);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <form
      role="search"
      aria-label={label}
      onSubmit={submit}
      className={cx(
        'flex h-12 w-full items-center gap-1 rounded-jc-control bg-jc-surface pl-4 pr-1 shadow-jc-sm ring-1 ring-inset ring-jc-border-control',
        'focus-within:ring-2 focus-within:ring-jc-ink',
        className,
      )}
    >
      <Icon icon={MagnifyingGlass} size="md" className="shrink-0 text-jc-ink-secondary" />
      <input
        ref={ref}
        type="search"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        aria-label={queryLabel}
        placeholder={placeholder}
        enterKeyHint="search"
        className="h-full min-w-0 flex-1 bg-transparent px-2 text-base text-jc-ink outline-none placeholder:text-jc-ink-secondary [&::-webkit-search-cancel-button]:hidden"
      />
      {place != null && onOpenPanel ? (
        <button
          type="button"
          onClick={onOpenPanel}
          aria-label={placeLabel}
          // Icon only on phones (the place is in the accessible name), the place written out from sm.
          className="inline-flex size-10 shrink-0 items-center justify-center gap-1.5 rounded-jc-control bg-jc-elevated text-sm font-semibold text-jc-ink hover:bg-jc-elevated-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus sm:w-auto sm:max-w-44 sm:px-4"
        >
          <Icon icon={MapPin} size="sm" className="shrink-0" />
          <span className="hidden truncate sm:inline">{place}</span>
        </button>
      ) : null}
      <button
        type="submit"
        className="inline-flex h-10 shrink-0 items-center rounded-jc-control bg-jc-cta px-4 text-sm font-semibold whitespace-nowrap text-jc-on-cta tabular-nums hover:bg-jc-cta-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-surface"
      >
        {showCount ? (
          <>
            {liveCount}
            <Icon icon={ArrowRight} size="sm" className="ml-1" />
          </>
        ) : (
          submitLabel
        )}
      </button>
    </form>
  );
}
